import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { ApplicantForm, validateApplicant } from "@/components/ApplicantForm";
import { DocumentsUpload } from "@/components/DocumentsUpload";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PayMethods } from "@/components/PayMethods";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, ArrowLeft, GraduationCap } from "lucide-react";
import { APPLICATION_FEE } from "@/lib/aps";

export default function ApplyBatch() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null); // [{university, name, logo, courses:[{id,name,campus}]}]
  const [selections, setSelections] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({});
  const [docs, setDocs] = useState({});
  const [dupes, setDupes] = useState([]);
  const [appFee, setAppFee] = useState(APPLICATION_FEE);
  const [popiaConsent, setPopiaConsent] = useState(false);
  const [priorLogins, setPriorLogins] = useState({});
  const setPrior = (uni, field) => (e) => setPriorLogins((p) => ({ ...p, [uni]: { ...(p[uni] || {}), [field]: e.target.value } }));
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    const raw = sessionStorage.getItem("yva_cart");
    const sels = raw ? JSON.parse(raw) : [];
    if (!sels.length) {
      toast.error("No universities selected. Please choose courses first.");
      navigate("/dashboard/courses");
      return;
    }
    setSelections(sels);
    api.get("/settings/fees").then(({ data }) => setAppFee(data.fees.application_fee)).catch(() => {});
    api.get("/courses/qualifying").then(({ data }) => {
      const all = data.courses || [];
      const built = sels.map((s) => {
        const courses = all.filter((c) => c.university === s.university && s.course_ids.includes(c.id));
        const first = courses[0] || {};
        return { university: s.university, name: first.university_name, logo: first.university_logo, courses };
      }).filter((g) => g.courses.length);
      if (!built.length) {
        toast.error("Your selection is no longer available. Please choose again.");
        navigate("/dashboard/courses");
        return;
      }
      setGroups(built);
      api.get("/applications/existing").then(({ data }) => {
        const applied = new Set(data.universities || []);
        setDupes(built.filter((g) => applied.has(g.university)).map((g) => g.name));
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    const missing = validateApplicant(f);
    if (missing.length) {
      toast.error(`Please complete all required fields. Missing: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ` and ${missing.length - 3} more` : ""}.`);
      return;
    }
    if (!popiaConsent) { toast.error("Please tick the POPIA consent box to continue."); return; }
    if (!docs.id_copy || (!docs.matric && !docs.results)) {
      toast.error("Please upload your certified ID copy and your matric certificate or results.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: batch } = await api.post("/applications/batch", {
        selections: selections.map((s) => ({ ...s, prior_login: priorLogins[s.university]?.reference || priorLogins[s.university]?.username ? priorLogins[s.university] : null })),
        applicant: f,
      });
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data: pay } = await api.post("/payments/initialize", {
        type: "application_batch",
        batch_id: batch.batch_id,
        callback_url,
      });
      sessionStorage.removeItem("yva_cart");
      window.location.href = pay.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  if (!groups)
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>
      </div>
    );

  const total = groups.length * appFee;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard/courses")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2447] mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to courses
        </button>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Combined Application</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1">
          Applying to {groups.length} universit{groups.length !== 1 ? "ies" : "y"}
        </h1>
        <p className="text-slate-600 mt-2">Fill in your details once — it will be used for all applications below. Pay once for everything.</p>

        <div className="mt-6 space-y-3" data-testid="batch-summary">
          {groups.map((g) => (
            <div key={g.university} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-start gap-4"
              data-testid={`batch-uni-${g.university}`}>
              <img src={g.logo} alt={g.name} className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-100 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display font-bold text-[#0B2447]">{g.name}</h3>
                  <Badge className="border-0 bg-[#F5C32C]/20 text-[#8a6d0b]">R{appFee.toFixed(2)}</Badge>
                </div>
                <div className="mt-2 space-y-1">
                  {g.courses.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <GraduationCap className="w-4 h-4 text-[#F5C32C] shrink-0" /> {c.name}
                      <span className="text-slate-400 text-xs">· {c.campus}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <h2 className="font-display text-xl font-bold text-[#0B2447] mb-4">Your details</h2>
          <ApplicantForm f={f} set={set} />
        </motion.div>

        {dupes.length > 0 && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800" data-testid="duplicate-warning">
            <strong>Heads up:</strong> you've already applied to {dupes.join(", ")}. If you apply again and we find an existing application, this fee is <strong>non-refundable</strong>.
          </div>
        )}

        <div className="mt-6 bg-[#0B2447]/[0.03] border border-[#0B2447]/15 rounded-xl p-4" data-testid="popia-consent-box">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={popiaConsent} onChange={(e) => setPopiaConsent(e.target.checked)} data-testid="popia-consent-checkbox" className="mt-1 w-4 h-4 accent-[#0B2447]" />
            <span className="text-sm text-slate-700">I consent, under the <strong>Protection of Personal Information Act (POPIA)</strong>, to Your Varsity Application securely storing and using my documents (ID copy, matric certificate/results) and details <strong>solely to process my university application(s)</strong>. I understand my information will not be shared for any other purpose.</span>
          </label>
        </div>

        <div className="mt-6">
          <DocumentsUpload onChange={setDocs} disabled={!popiaConsent} />
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="prior-login-section">
          <h3 className="font-display font-bold text-[#0B2447]">Applied to any of these before?</h3>
          <p className="text-sm text-slate-500 mt-1">If you've already applied directly on a university's own portal, add those login details so we can continue your existing application. Leave blank if not.</p>
          <div className="mt-4 space-y-4">
            {groups.map((g) => (
              <div key={g.university} className="border border-slate-100 rounded-lg p-3" data-testid={`prior-${g.university}`}>
                <div className="text-sm font-semibold text-[#0B2447] mb-2">{g.name}</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <Input placeholder="Portal username" value={priorLogins[g.university]?.username || ""} onChange={setPrior(g.university, "username")} data-testid={`prior-user-${g.university}`} />
                  <Input placeholder="Portal password" value={priorLogins[g.university]?.password || ""} onChange={setPrior(g.university, "password")} data-testid={`prior-pass-${g.university}`} />
                  <Input placeholder="Reference number" value={priorLogins[g.university]?.reference || ""} onChange={setPrior(g.university, "reference")} data-testid={`prior-ref-${g.university}`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sm:sticky bottom-4 mt-8">
          <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-5 flex flex-wrap items-center justify-between gap-4">
            <PayMethods className="basis-full" />
            <div>
              <div className="font-display text-2xl font-bold text-[#0B2447]" data-testid="batch-total">R{total.toFixed(2)}</div>
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> {groups.length} × R{appFee.toFixed(2)} · one secure Paystack payment
              </p>
            </div>
            <Button onClick={submit} disabled={submitting} data-testid="batch-submit-btn"
              className="bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 px-8">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay R${total.toFixed(2)} & Submit All`}
            </Button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-2">
            Payment may take up to 60 seconds to load. Please wait and don't refresh.
          </p>
        </div>
      </div>
    </div>
  );
}
